// Apolo IDE — PlanCanvasHeader (sprint plan-tab-v1, E2).
//
// Nome do plano + status + indicador de save + ação "exportar pra Code"
// (placeholder; ativo só em E7).

import { useEffect, useRef, useState } from 'react';
import { BarChart3, CheckCircle2, Cloud, CloudOff, Download, LayoutDashboard, Loader2 } from 'lucide-react';

const SAVE_LABEL = {
  idle:    { label: 'Salvo',         tone: 'text-apolo-success',     Icon: CheckCircle2 },
  pending: { label: 'Aguardando',    tone: 'text-apolo-textTertiary',Icon: Cloud },
  saving:  { label: 'Salvando…',     tone: 'text-apolo-plan-accent', Icon: Loader2 },
  error:   { label: 'Erro ao salvar',tone: 'text-apolo-error',       Icon: CloudOff },
};

export default function PlanCanvasHeader({ plan, savingState = 'idle', blockCount = 0, onRename, onOpenSprintBuilder, planView = 'canvas', onPlanViewChange }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(plan?.name || '');
  const inputRef = useRef(null);

  useEffect(() => { setDraft(plan?.name || ''); }, [plan?.id, plan?.name]);
  useEffect(() => { if (editing) setTimeout(() => inputRef.current?.select(), 30); }, [editing]);

  const status = SAVE_LABEL[savingState] || SAVE_LABEL.idle;
  const Icon = status.Icon;

  function commit() {
    setEditing(false);
    const trimmed = draft.trim();
    if (trimmed && trimmed !== plan?.name) onRename?.(trimmed);
    else setDraft(plan?.name || '');
  }

  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-apolo-borderSubtle bg-apolo-surface1/70 px-4 backdrop-blur-md">
      {/* Identidade */}
      <span
        aria-hidden="true"
        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-base"
        style={{ background: 'var(--apolo-plan-accentMuted)', color: 'var(--apolo-plan-accent)' }}
      >
        {plan?.emoji || '📋'}
      </span>

      {/* Nome (clique pra editar) */}
      {editing ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setDraft(plan?.name || ''); setEditing(false); } }}
          maxLength={120}
          className="h-7 rounded-md border border-apolo-plan-accentBorder bg-apolo-bg px-2 text-sm focus:outline-none focus:ring-2 focus:ring-apolo-plan-accentRing"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex items-center gap-1 truncate rounded px-1 text-sm font-semibold tracking-tight text-apolo-textPrimary hover:bg-apolo-surface2"
          aria-label="Renomear plano"
        >
          {plan?.name || 'Sem nome'}
        </button>
      )}

      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wide ${planStatusClass(plan?.status)}`}>
        {plan?.status || 'draft'}
      </span>

      <span className="text-[11px] text-apolo-textTertiary">{blockCount} {blockCount === 1 ? 'bloco' : 'blocos'}</span>

      {/* Toggle Canvas | Charts */}
      {onPlanViewChange ? (
        <div role="tablist" aria-label="Modo de visualização" className="ml-auto inline-flex items-center gap-0.5 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 p-0.5">
          <button
            type="button" role="tab" aria-selected={planView === 'canvas'}
            onClick={() => onPlanViewChange('canvas')}
            title="Canvas"
            className={`inline-flex h-6 items-center gap-1 rounded px-2 text-[11px] font-medium transition ${planView === 'canvas' ? 'bg-apolo-plan-accentMuted text-apolo-plan-accent' : 'text-apolo-textSecondary hover:text-apolo-textPrimary'}`}
          >
            <LayoutDashboard size={12} /> Canvas
          </button>
          <button
            type="button" role="tab" aria-selected={planView === 'charts'}
            onClick={() => onPlanViewChange('charts')}
            title="Charts"
            className={`inline-flex h-6 items-center gap-1 rounded px-2 text-[11px] font-medium transition ${planView === 'charts' ? 'bg-apolo-plan-accentMuted text-apolo-plan-accent' : 'text-apolo-textSecondary hover:text-apolo-textPrimary'}`}
          >
            <BarChart3 size={12} /> Charts
          </button>
        </div>
      ) : null}

      <span className={`inline-flex items-center gap-1.5 text-[11px] ${onPlanViewChange ? 'ml-2' : 'ml-auto'}`}>
        <Icon size={12} className={`${status.tone} ${savingState === 'saving' ? 'animate-spin' : ''}`} aria-hidden="true" />
        <span className={status.tone}>{status.label}</span>
      </span>

      {/* Botão Export (E7) — agora ativo */}
      <button
        type="button"
        onClick={onOpenSprintBuilder}
        disabled={!onOpenSprintBuilder}
        title="Exportar pra aba Code (Sprint Directive)"
        className="ml-2 inline-flex h-8 items-center gap-1 rounded-md border border-apolo-plan-accentBorder bg-apolo-plan-accentMuted px-2.5 text-xs font-semibold text-apolo-plan-accent transition hover:brightness-110 disabled:opacity-60"
      >
        <Download size={12} /> Exportar
      </button>
    </div>
  );
}

function planStatusClass(status) {
  if (status === 'active')   return 'border-apolo-success/40 bg-apolo-success/10 text-apolo-success';
  if (status === 'exported') return 'border-apolo-plan-accentBorder bg-apolo-plan-accentMuted text-apolo-plan-accent';
  if (status === 'archived') return 'border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textTertiary';
  return 'border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textTertiary'; // draft
}
